import React from 'react'
import moment from 'moment'
import { ReactReduxContext, ReactReduxContextValue } from 'react-redux'
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  Column,
  ColumnState,
  EditableCallbackParams,
  GetQuickFilterTextParams,
  GridOptions,
  HeaderValueGetterParams,
  ICellEditor,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community'
import { findLastIndex, intersection, isEmpty, isNil, uniq } from 'lodash-es'
import {
  AxeTradeCondition,
  CollectionType,
  DealDetail,
  IsBestQuote,
  NegotiationDetail,
  NegotiationOrAuditDetail,
  NegotiationPackageDetail,
  UserPreference
} from 'api'
import { ModalConsumerProps, ModalContext } from 'view/shared/modal'
import { DefaultCellEditor } from '../renderers/DefaultCellEditor'
import { GroupHeaderRenderer } from '../renderers/GroupHeaderRenderer'
import { GroupCellRenderer } from '../renderers/GroupCellRenderer'
import { AxeHelper, TradeConditionFlagHelper } from 'model'
import { EnvironmentContext, EnvironmentProps } from 'view/shared/environment'
import { booleanValueFormatter, parseCollectionKey } from 'view/shared/datagrid/modelFormatters'
import { hasNegotiations } from '../renderers/negotiationDataUtil'
import { BestPriceType, GridId } from 'store/grid/gridTypes'
import { CollectionColumnId, ContainerCollectionColumnId, ROE, ROS } from 'view/routes/ColumnNames'
import { agGridIcons } from '../styles/agGridIcons'
import { iconSet } from '../../icons'
import { KeyCodes } from 'utils'
import { Locale, Translation } from 'locale'
import { ValueGetterMapper } from 'selectors/negotiations'
import { CellClickedEvent, CellKeyDownEvent, ICellRendererComp, RowClassParams } from 'ag-grid-community'
import { isNotNil } from 'utils/predicate'
import { augmentAgGridContextWithDataGridContext } from '../DataGridContext'
import { MasterDetailCellRenderer } from '../renderers'

export function dateComparator(filterValue: Date, cellValue: string) {
  const cellDate = moment(cellValue)
  const filterDate = moment(filterValue)

  if (cellDate.isBefore(filterDate, 'day')) {
    return -1
  } else if (cellDate.isAfter(filterDate, 'day')) {
    return 1
  } else {
    return 0
  }
}

// Need to define browserDatePicker for OpenFin
export const dateFilterParams = {
  filterParams: {
    browserDatePicker: 'datePicker',
    comparator: dateComparator
  }
}

export function axeTradeConditionKeyCreator({ value }: { value: AxeTradeCondition | undefined }) {
  if (value) {
    return `${AxeHelper.getName(value.isAxed)}, ${TradeConditionFlagHelper.getName(value.tradeCondition)}`
  } else {
    return ''
  }
}

export interface AgGridContext {
  updateCellValue: (rowId: string, rowItem: any, field: String, newValue: any) => void
  changeCellValue: (rowId: string, rowItem: any, field: string) => void
  modalContext: ModalConsumerProps
  reduxContext: ReactReduxContextValue
  environmentContext: EnvironmentProps
}

interface AgGridParams {
  context: AgGridContext
}

export const withAllContext = <P extends AgGridParams>(Component: React.ComponentType<P>): React.ComponentType<P> =>
  React.forwardRef((props, ref) => (
    <ModalContext.Provider value={props.context.modalContext}>
      <ReactReduxContext.Provider value={props.context.reduxContext}>
        <EnvironmentContext.Provider value={props.context.environmentContext}>
          <Component {...props} ref={ref} />
        </EnvironmentContext.Provider>
      </ReactReduxContext.Provider>
    </ModalContext.Provider>
  )) as React.ComponentType<P>

export interface TypedCellRendererParams<T, V = any, S = TypedContext<T>> extends ICellRendererParams<T, V> {
  node: IRowNode<any>
  context: S
}
interface AgGridReactOptions<T> {
  context: TypedContext<T>
}

export interface AgGridReact<T> {
  gridOptions: AgGridReactOptions<T>
}

interface NegotiationCellRendererProps extends TypedCellRendererParams<NegotiationDetail> {
  data: NegotiationPackageDetail
}

interface CreateCellRendererOptions<P> {
  FallbackComponent?: React.ComponentType<P>
  forceComponent?: boolean
  valueGetterMapper: ValueGetterMapper
}

export function createCellRenderer<P extends NegotiationCellRendererProps>(
  Component: React.ComponentType<P>,
  options: CreateCellRendererOptions<P>
) {
  const { forceComponent = false, FallbackComponent, valueGetterMapper } = options
  return class CellRenderer extends React.Component<P> {
    componentRef = React.createRef<ICellEditor>()

    render() {
      const {
        props: { data, node }
      } = this

      const props = valueGetterMapper({
        ...this.props,
        context: this.props.context
      })

      if (node && node.group) {
        return <Component {...props} ref={this.componentRef} />
      }

      if (forceComponent) {
        return <Component {...this.props} />
      }

      if (!hasNegotiations(data) || !hasNegotiations(node.data)) {
        return FallbackComponent ? <FallbackComponent {...this.props} ref={this.componentRef} /> : null
      }

      if (hasNegotiations(props.data as any)) {
        return null
      }

      return <Component {...props} ref={this.componentRef} />
    }

    componentDidMount() {
      const {
        props: { api }
      } = this

      if (api) {
        api.addEventListener('rowDataUpdated', this.refresh)
      } else {
        throw new Error('Grid API is not set')
      }
    }

    componentWillUnmount() {
      const {
        props: { api }
      } = this

      if (api) {
        !api.isDestroyed() && api.removeEventListener('rowDataUpdated', this.refresh)
      } else {
        throw new Error('Grid API is not set')
      }
    }

    public refresh = () => {
      this.forceUpdate()
      return true
    }

    public getValue() {
      if (this.componentRef.current) {
        return this.componentRef.current.getValue()
      }

      return null
    }

    public focusIn = () => {
      if (this.componentRef.current?.focusIn) {
        this.componentRef.current.focusIn()
      }
    }
  }
}

const IGNORED_KEYCODES = [KeyCodes.ENTER]

export const defaultCellEditor = augmentAgGridContextWithDataGridContext(DefaultCellEditor)

export const defaultComparator = (valueA: any, valueB: any): number => {
  if (isNil(valueA) && isNil(valueB)) {
    return 0
  } else if (isNil(valueA)) {
    return -1
  } else if (isNil(valueB)) {
    return 1
  }

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB
  }

  if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
    return valueA === true ? 1 : -1
  }

  return 0
}

interface GridOptionRequiredGetRowClass extends GridOptions {
  getRowClass: (params: any) => string | string[]
  defaultColDef: ColDef
}

export const gridOptionsBase: GridOptionRequiredGetRowClass = {
  animateRows: true,
  autoSizePadding: 0.1,
  defaultColDef: {
    cellClassRules: {
      'ag-cell-custom-renderer': (params: CellClassParams) => params.colDef.cellRenderer
    },
    cellDataType: true,
    cellEditor: defaultCellEditor,
    editable: false,
    enableRowGroup: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      convertValuesToStrings: true
    },
    lockPinned: false,
    resizable: true,
    sortable: true,
    comparator: defaultComparator,
    suppressMenu: true,
    valueSetter: (_params: ValueSetterParams) => false,
    minWidth: 50,
    floatingFilter: true,
    suppressKeyboardEvent: params => {
      // return true (to suppress) if editing and user hit up/down keys
      return params.editing && IGNORED_KEYCODES.includes(params.event.keyCode)
    }
  },
  editType: 'fullRow',
  floatingFiltersHeight: 36,
  getRowClass: params => {
    const node: IRowNode = params.node
    if (node.master) {
      return 'master-row-level'
    } else if (node.level === 0 && node.group === true) {
      return 'group-row-level'
    } else if (node.detail) {
      return 'detail-row-level'
    } else if (node.level > 0 && node.group === true) {
      return 'subgroup-row-level'
    } else {
      return 'nested-row-level'
    }
  },
  groupDefaultExpanded: -1,
  groupHeaderHeight: 0,
  groupSelectsChildren: true,
  groupSelectsFiltered: true,
  groupDisplayType: 'custom',
  copyHeadersToClipboard: true,
  copyGroupHeadersToClipboard: true,
  headerHeight: 37,
  icons: agGridIcons,
  rowGroupPanelShow: 'always',
  rowHeight: 36,
  rowSelection: 'multiple',
  // singleClickEdit: true,
  stopEditingWhenCellsLoseFocus: false,
  // accentedSort: true,
  suppressAggFuncInHeader: true,
  suppressBrowserResizeObserver: true,
  suppressClickEdit: true,
  suppressColumnVirtualisation: true,
  suppressContextMenu: true,
  suppressDragLeaveHidesColumns: true,
  suppressRowGroupHidesColumns: true,
  suppressLoadingOverlay: true,
  suppressRowClickSelection: true
}

export enum StandardGridColumnId {
  MASTER_DETAIL = 'MASTER_DETAIL',
  CHECKBOX = 'CHECKBOX',
  GROUPING = 'GROUPING',
  ACTIONS = 'ACTIONS'
}

export const createMasterDetailRowColDef = <T,>({ isRowMaster }: { isRowMaster: (rowData: T) => boolean }): ColDef => ({
  colId: StandardGridColumnId.MASTER_DETAIL,
  cellRenderer: MasterDetailCellRenderer,
  equals: () => false,
  width: 20,
  cellRendererParams: {
    isRowMaster
  },
  filter: false,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  pinned: 'left' as const,
  enableRowGroup: false,
  lockPinned: true,
  lockPosition: true,
  lockVisible: true,
  suppressMovable: true,
  sortable: false,
  resizable: false
})

export const groupingRowColDef: ColDef = {
  cellRenderer: GroupCellRenderer,
  cellRendererParams: {
    suppressCount: true //remove number in Group Column
  },
  colId: StandardGridColumnId.GROUPING,
  filter: false,
  headerName: Locale.translate(Translation.TC_GROUPING),
  headerComponent: GroupHeaderRenderer,
  hide: true,
  lockPinned: true,
  lockPosition: true,
  pinned: 'left',
  suppressMovable: true,
  width: 200
}

export const tradesGridGroupingRowColDef: ColDef = {
  ...groupingRowColDef,
  cellRendererParams: {
    ...groupingRowColDef.cellRendererParams,
    columnKeyFormatters: {
      [CollectionColumnId.COLLECTION_NAME]: (key: string) => parseCollectionKey(key),
      [ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME]: (key: string) => {
        return parseCollectionKey(key)
      },
      [ROE]: (key: string) => booleanValueFormatter(key),
      [ROS]: (key: string) => booleanValueFormatter(key)
    },
    columnIcons: {
      [CollectionColumnId.COLLECTION_NAME]: (groupLabel: string, leafChildren: IRowNode[]) =>
        groupLabel === Locale.translate(Translation.TC_COLLECTION_UNASSIGNED) ||
        !isCollectionTypeAllOrNothingOrOneCancelsOthers(leafChildren)
          ? undefined
          : iconSet.collectionIcon
    }
  }
}

const isCollectionTypeAllOrNothingOrOneCancelsOthers = (leafChildren: IRowNode[]) => {
  return (
    leafChildren.length > 0 &&
    leafChildren[0].data.collection &&
    (leafChildren[0].data.collection.type === CollectionType.ALL_OR_NOTHING ||
      leafChildren[0].data.collection.type === CollectionType.ONE_CANCELS_OTHERS)
  )
}

interface ColFilterDef {
  shouldApply: boolean
  colIds: string[]
}

type ColIdSelector = (c: ColDef) => string | undefined
export interface FilteredColumnDefsBuilder {
  without: (colIds: string[]) => FilteredColumnDefsBuilder
  withoutIf: (apply: boolean, colIds: string[]) => FilteredColumnDefsBuilder
  withColIdSelector: (columnSelector: ColIdSelector) => FilteredColumnDefsBuilder
  build: () => (ColGroupDef | ColDef)[]
}

export const isCollectionColumn = (col: Column) => col.getColId() === CollectionColumnId.COLLECTION_NAME

export const filteredColumnDefsBuilder = (colDefs: (ColGroupDef | ColDef)[]) => {
  const allColDefs = colDefs
  const filterDefs: ColFilterDef[] = []
  let colIdSelectors: ColIdSelector[] = [c => c.colId]

  const filterColDefs = (colDefSubset: (ColGroupDef | ColDef)[]) =>
    colDefSubset.reduce<(ColGroupDef | ColDef)[]>((acc, colDefOrColGroupDef) => {
      if (isColGroupDef(colDefOrColGroupDef) && colDefOrColGroupDef.children) {
        acc.push({
          ...colDefOrColGroupDef,
          children: filterColDefs(colDefOrColGroupDef.children)
        })
        return acc
      }

      const colDef = colDefOrColGroupDef as ColDef

      if (isEmpty(colIdSelectors.map(s => s(colDef)).filter(isNotNil))) {
        acc.push(colDef)
        return acc
      }

      if (
        filterDefs.some(filterDef => filterDef.shouldApply && filterDef.colIds.some(colId => colIdSelectors.some(s => s(colDef) === colId)))
      ) {
        return acc
      }

      acc.push(colDef)
      return acc
    }, [])

  const self: FilteredColumnDefsBuilder = {
    without: (colIds: string[]) => {
      filterDefs.push({ shouldApply: true, colIds })
      return self
    },
    withoutIf: (shouldApply: boolean, colIds: string[]) => {
      filterDefs.push({ shouldApply, colIds })
      return self
    },
    withColIdSelector: _colIdSelector => {
      colIdSelectors = [...colIdSelectors, _colIdSelector]
      return self
    },
    build: () => filterColDefs(allColDefs)
  }
  return self
}

export const isColGroupDef = (counterpartyColDef: ColGroupDef | ColDef): counterpartyColDef is ColGroupDef =>
  'children' in counterpartyColDef

export const flattenColumnDefs = (columnDefs: (ColDef | ColGroupDef)[]): ColDef[] =>
  columnDefs.reduce((acc, columnDef) => {
    const colDef = isColGroupDef(columnDef) ? flattenColumnDefs(columnDef.children) : columnDef
    return acc.concat(colDef)
  }, [] as ColDef[])

export interface TypedContext<T> extends AgGridContext {
  bestPriceType: BestPriceType
  userPreference: UserPreference
  groupRowData?: GroupRowData<T>
  gridId: GridId
}

export const isBestNegotiationRow = (params: TypedCellClassParams<NegotiationOrAuditDetail>) => {
  const { data } = params
  return isNegotiationOrAuditDetailBestQuote(data)
}

export const isNegotiationOrAuditDetailBestQuote = (n: NegotiationOrAuditDetail | undefined) => {
  if (isNil(n) || hasNegotiations(n) || !('meta' in n)) {
    return false
  }

  return n.meta?.isBestQuote === IsBestQuote.YES
}

export const isSelectedForRelatedDealTrades = (params: TypedCellClassParams<DealDetail>) => {
  const { data } = params
  if (isNil(data)) {
    return false
  }

  return data.isSelectedForRelatedDealTrades
}

export const isSelectedForRelatedOpenRepoTrades = (params: TypedCellClassParams<DealDetail>) => {
  const { data } = params
  if (isNil(data)) {
    return false
  }

  return data.isSelectedForRelatedOpenRepoTrades
}

// A second generic type S for the staging grid where the type of the group row data isn't the type of the row data
// Defaults to T
export interface TypedValueGetterParams<T, S = T> extends ValueGetterParams {
  data: T | undefined
  node: TypedRowNode<T>
  context: TypedContext<S>
}
export interface TypedHeaderValueGetterParams<T, S = T> extends HeaderValueGetterParams {
  data: T | undefined
  node: TypedRowNode<T>
  context: TypedContext<S>
}

export interface TypedValueFormatterParams<T, V, S = unknown> extends ValueFormatterParams<T, V> {
  value: V
  node: TypedRowNode<T>
  context: TypedContext<S>
}

export interface TypedEditableCallbackParams<T, S = T> extends EditableCallbackParams {
  data: T | undefined
  context: TypedContext<S>
}

export interface TypedGetQuickFilterTextParams<T, S = T> extends GetQuickFilterTextParams {
  value: T | null
  context: TypedContext<S>
}

export interface TypedCellClassParams<T, S = T> extends CellClassParams {
  data: T | undefined
  context: TypedContext<S>
}
export interface TypedRowClassParams<T, S = T> extends RowClassParams<T> {
  data: T | undefined
  context: TypedContext<S>
}

export interface TypedRowNode<T> extends IRowNode {
  data: T | undefined
  allLeafChildren: TypedRowNode<T>[]
}

export interface KeyCreatorParams<T> {
  value: T | undefined
}

export type GroupRowData<T> = Record<string, Record<string, T>>

export interface TypedCellClickedEvent<T, S = T> extends CellClickedEvent {
  data: T | undefined
  node: TypedRowNode<T>
  context: TypedContext<S>
}

export const getRestorableColumnSettings = (columnState: readonly ColumnState[], columns: Column[]): ColumnState[] => {
  const columnsByColId = columns.reduce<Record<string, Column>>(
    (result, col) => ({
      ...result,
      [col.getColId()]: col
    }),
    {}
  )

  const columnStateByColId = columnState.reduce<Record<string, ColumnState>>((result, col) => {
    return col.colId
      ? {
          ...result,
          [col.colId]: col
        }
      : result
  }, {})

  const colStateIds = Object.keys(columnStateByColId)
  const colIds = Object.keys(columnsByColId)
  const allColIds = uniq([...colStateIds, ...colIds])

  const getLikeForLikeCounterpartyColumnState = (colDef: ColDef) => {
    const originalColId = colDef.cellRendererParams?.originalColId
    if (!originalColId) {
      return colDef
    }

    const [similarColumnId] = intersection(
      columns.filter(col => col.getColDef().cellRendererParams?.originalColId === originalColId)?.map(c => c.getColId()),
      colStateIds
    )
    return similarColumnId ? { ...colDef, ...columnStateByColId[similarColumnId] } : colDef
  }

  return Object.values(allColIds).reduce<ColumnState[]>((result, colId) => {
    const colState = columnStateByColId[colId]
    const col = columnsByColId[colId]

    // Column removed, don't restore
    if (isNil(col)) {
      return result
    }

    // Column state exists
    if (!isNil(colState)) {
      return result.concat(colState)
    }

    const colDef = col.getColDef()

    // Attempt to find insert position if this is a counterparty column
    const position = findLastIndex(result, thisCol => {
      if (!thisCol.colId || isNil(colDef.cellRendererParams?.originalColId)) {
        return false
      }
      const thisColDef = columnsByColId[thisCol.colId].getColDef()
      return !isNil(thisColDef.cellRendererParams?.originalColId)
    })

    // New Counterparty column, insert after last counterparty column
    if (position !== -1) {
      return [
        ...result.slice(0, position + 1),
        {
          ...getLikeForLikeCounterpartyColumnState(colDef), // apply state from previous counterparty column
          colId
        },
        ...result.slice(position + 1)
      ]
    }

    // New column, generate default state and append to end
    return result.concat({
      ...colDef,
      colId
    })
  }, [])
}

export function setNodesSelected(nodes: IRowNode[], value: boolean) {
  // for improved performance we defer side effects until the last row selection
  const [last, ...rest] = nodes.reverse()
  rest.map(n => n.setSelected(value, false))
  if (last) {
    // ag-grid will not dispatch events if the node selection state is unchanged.
    // Since we have no record of previously selected nodes but definitely want the side effects
    // ensure there is a state change
    last.setSelected(!value, false)
    last.setSelected(value)
  }
}

export const createNestedColumnMapper = (mapFn: (c: ColDef | ColGroupDef) => ColDef | ColGroupDef) => (
  c: ColDef | ColGroupDef
): ColDef | ColGroupDef => (isColGroupDef(c) ? { ...c, children: c.children.map(createNestedColumnMapper(mapFn)) } : mapFn(c))

export const isCellRendererClass = (candidate: any): candidate is ICellRendererComp => {
  if (
    typeof candidate === 'function' &&
    typeof candidate?.prototype?.init === 'function' &&
    typeof candidate?.prototype?.getGui === 'function' &&
    typeof candidate?.prototype?.destroy === 'function' &&
    typeof candidate?.prototype?.refresh === 'function'
  ) {
    return true
  }

  return false
}

export const isCopyKeyEvent = (evt: CellKeyDownEvent) => {
  return (
    evt.event instanceof KeyboardEvent &&
    (evt.event.metaKey || evt.event.ctrlKey) &&
    evt.event.keyCode === KeyCodes.C &&
    !evt.event.defaultPrevented
  )
}
